import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getUniId } from '@/core/utils/extractCookieId';
import { ExportButton } from '@/core/components/ExportButton/ExportButton';

const BdsSwitch = 'bds-switch' as any;

const STORAGE_KEY_BASES = {
  highlightTerms: 'MFE_PROPS_SHOW_TERMS',
  groupPublications: 'MFE_PROPS_GROUP_POSTS',
  answered: 'MFE_PROPS_ANSWERED',
} as const;

const storageUniId = getUniId();
const STORAGE_KEYS = {
  highlightTerms: `${STORAGE_KEY_BASES.highlightTerms}_${storageUniId}`,
  groupPublications: `${STORAGE_KEY_BASES.groupPublications}_${storageUniId}`,
  answered: `${STORAGE_KEY_BASES.answered}_${storageUniId}`,
} as const;

const getStoredToggleValue = (storageKey: string, fallbackValue: boolean) => {
  try {
    const storedValue = localStorage.getItem(storageKey);

    if (storedValue === 'true') {
      return true;
    }

    if (storedValue === 'false') {
      return false;
    }
  } catch {
    // ignore empty
  }

  return fallbackValue;
};

const SwitchItem: React.FC<{ label: string; checked: boolean; onChange: () => void; name: string; size: 'short' }> = ({ label, checked, onChange, name, size }) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onChange();
    el.addEventListener('bdsChange', handler);
    return () => el.removeEventListener('bdsChange', handler);
  }, [onChange]);

  return (
    <label className="sort-submenu__item">
      <span className="sort-submenu__label">{label}</span>
      <BdsSwitch ref={ref} name={name} checked={checked} size={size} data-sort-switch={name} />
    </label>
  );
};

export const SortSubmenu: React.FC = () => {
  const { t } = useTranslation();
  const [highlightTerms, setHighlightTerms] = useState(() => getStoredToggleValue(STORAGE_KEYS.highlightTerms, true));
  const [groupPublications, setGroupPublications] = useState(() => getStoredToggleValue(STORAGE_KEYS.groupPublications, false));
  const [answered, setAnswered] = useState(() => getStoredToggleValue(STORAGE_KEYS.answered, false));

  const dispatch = useCallback((eventName: string, detail?: unknown) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }, []);

  const handleToggle = useCallback((setter: React.Dispatch<React.SetStateAction<boolean>>, eventName: string) => {
    setter((prev) => {
      const next = !prev;
      dispatch(eventName, { active: next });
      return next;
    });
  }, [dispatch]);

  return (
    <div className="sort-submenu">
      <SwitchItem
        size="short"
        name="highlightTerms"
        label={t('sortBy.submenu.highlightTerms')}
        checked={highlightTerms}
        onChange={() => handleToggle(setHighlightTerms, 'mfe-sort-highlight-terms')}
      />
      
      <SwitchItem
        size="short"
        name="groupPublications"
        label={t('sortBy.submenu.groupPublications')}
        checked={groupPublications}
        onChange={() => handleToggle(setGroupPublications, 'mfe-sort-group-publications')}
      />
      
      <SwitchItem
        size="short"
        name="answered"
        label={t('sortBy.submenu.answered')}
        checked={answered}
        onChange={() => handleToggle(setAnswered, 'mfe-sort-answered')}
      />

      <ExportButton />
    </div>
  );
};